import { useEffect, useState } from "react";
import { Modal } from "antd";
import { nanoid } from "nanoid";

export const TEMP_ORG_PREFIX = "__temp_org__";

export interface InviteOrgModalSaveData {
	tempId: string;
	orgName: string;
	orgEmail: string;
	userEmail: string;
}

interface InviteOrgModalProps {
	open: boolean;
	orgLabel: string;
	initialValues?: InviteOrgModalSaveData;
	onCancel: () => void;
	onSave: (data: InviteOrgModalSaveData) => void;
}

export interface NewOrgData {
	tempId: string;
	name: string;
	email: string;
}

const InviteOrgModal = ({ open, orgLabel, initialValues, onCancel, onSave }: InviteOrgModalProps) => {
	const [orgName, setOrgName] = useState(initialValues?.orgName ?? "");
	const [orgEmail, setOrgEmail] = useState(initialValues?.orgEmail ?? "");
	const [userEmail, setUserEmail] = useState(initialValues?.userEmail ?? "");

	useEffect(() => {
		if (open) {
			setOrgName(initialValues?.orgName ?? "");
			setOrgEmail(initialValues?.orgEmail ?? "");
			setUserEmail(initialValues?.userEmail ?? "");
		}
	}, [open]);

	const isOrgEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgEmail);
	const isFormValid = orgName.trim().length > 0 && isOrgEmailValid;
	const isUserEmailValid = userEmail.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail);

	const handleSave = () => {
		onSave({ tempId: `${TEMP_ORG_PREFIX}${nanoid()}`, orgName, orgEmail, userEmail });
	};

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			footer={null}
			title={null}
			closable={false}
			className="ha-invite-org-modal"
			width={600}
		>
			<div className="ha-modal-header">
				<span className="ha-modal-title">{`Invite and assign a new ${orgLabel}`}</span>
			</div>

			<div className="ha-modal-fields">
				<div className="ha-modal-field-group">
					<label className="ha-modal-label" htmlFor="invite-org-name">
						Company name
					</label>
					<input id="invite-org-name" className="ha-input" value={orgName} onChange={e => setOrgName(e.target.value)} />
				</div>
				<div className="ha-modal-field-group">
					<label className="ha-modal-label" htmlFor="invite-org-email">
						{"Primary Contact (email)"}
					</label>
					<p className="ha-modal-field-hint">
						The official contact address for this company (e.g. info@company.com). Used for billing and general
						correspondence.
					</p>
					<input
						id="invite-org-email"
						className="ha-input"
						type="email"
						value={orgEmail}
						onChange={e => setOrgEmail(e.target.value)}
					/>
				</div>
			</div>

			<div className="ha-modal-divider">
				<span className="ha-modal-divider-label">User invitation</span>
			</div>

			<div className="ha-modal-fields">
				<div className="ha-modal-field-group">
					<label className="ha-modal-label" htmlFor="invite-user-email">
						User email
					</label>
					<p className="ha-modal-field-hint">
						The person who will manage this handover and property on behalf of{" "}
						{orgName ? <strong>{orgName}</strong> : `the ${orgLabel}`}. They'll receive a signup invitation at this
						address.
					</p>
					<input
						id="invite-user-email"
						className="ha-input"
						type="email"
						placeholder="e.g., john@company.com"
						value={userEmail}
						onChange={e => setUserEmail(e.target.value)}
					/>
				</div>
			</div>

			<div className="ha-modal-actions">
				<button type="button" className="ha-cancel-btn" onClick={onCancel}>
					Cancel
				</button>
				<button
					type="button"
					className="ha-modal-save-btn"
					disabled={!isFormValid || !userEmail || !isUserEmailValid}
					onClick={handleSave}
				>
					Save
				</button>
			</div>
		</Modal>
	);
};

export default InviteOrgModal;
